import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import "../../styles/orderHistory.css";
import { API_ENDPOINTS, IN_APP_BROWSERS } from "../../config/constants";

// Download icon SVG
const DownloadIcon = ({ size = 16, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 20 20" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path 
      d="M10 2v10m0 0l-4-4m4 4l4-4" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <rect x="3" y="16" width="14" height="2" rx="1" fill="currentColor" />
  </svg>
);

// Payment icon SVG
const PaymentIcon = ({ size = 16, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 20 20" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <rect x="2" y="6" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="2"/>
    <path d="M6 6V4C6 2.89543 6.89543 2 8 2H12C13.1046 2 14 2.89543 14 4V6" stroke="currentColor" strokeWidth="2"/>
    <circle cx="10" cy="11" r="2" fill="currentColor"/>
  </svg>
);

// Expand/Collapse icon component
const ExpandIcon = ({ isExpanded, className = "" }) => (
  <svg 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={`expand-icon ${isExpanded ? 'expanded' : ''} ${className}`}
    style={{ 
      minWidth: '20px', 
      minHeight: '20px',
      transition: 'transform 0.2s ease'
    }}
  >
    <path 
      d={isExpanded ? "M7 14L12 9L17 14" : "M9 6L14 12L9 18"} 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// View Details icon
const ViewIcon = ({ size = 16, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 20 20" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path 
      d="M10 3C5 3 1.73 7.11 1 10c.73 2.89 4 7 9 7s8.27-4.11 9-7c-.73-2.89-4-7-9-7z" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

// Helper for formatting date
function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Helper for formatting order ID
function formatOrderId(orderId) {
  if (!orderId) return "-";
  const formattedOrderId = String(orderId).padStart(6, "0");
  return `#${formattedOrderId}`;
}

// Helper for status badge with comprehensive status handling
function getStatusBadge(status) {
  if (!status) return null;
  const statusLower = status.toLowerCase().trim();
  let statusClass = 'status-badge ';
  let displayText = status;
  let icon = '';
  
  // Completed/Success statuses
  if (statusLower.includes('completed') || 
      statusLower.includes('delivered') || 
      statusLower.includes('success') ||
      statusLower.includes('paid')) {
    statusClass += 'status-completed';
    icon = '✅';
    displayText = statusLower.includes('paid') ? 'Paid' : 
                 statusLower.includes('delivered') ? 'Delivered' : 
                 statusLower.includes('success') ? 'Success' : 'Completed';
  } 
  // Processing/In Progress statuses
  else if (statusLower.includes('processing') || 
           statusLower.includes('in_progress') ||
           statusLower.includes('in-progress') ||
           statusLower.includes('progress')) {
    statusClass += 'status-processing';
    icon = '⚙️';
    displayText = 'Processing';
  }
  // Pending statuses
  else if (statusLower.includes('pending') || 
           statusLower.includes('waiting') ||
           statusLower.includes('initiated')) {
    statusClass += 'status-pending';
    icon = '⏳';
    displayText = statusLower.includes('waiting') ? 'Waiting' : 
                 statusLower.includes('initiated') ? 'Initiated' : 'Pending';
  }
  // Failed/Error statuses
  else if (statusLower.includes('failed') || 
           statusLower.includes('error') ||
           statusLower.includes('declined')) {
    statusClass += 'status-failed';
    icon = '❌';
    displayText = statusLower.includes('declined') ? 'Declined' : 
                 statusLower.includes('error') ? 'Error' : 'Failed';
  }
  // Cancelled/Refunded statuses
  else if (statusLower.includes('cancelled') || 
           statusLower.includes('canceled') ||
           statusLower.includes('refunded') ||
           statusLower.includes('refund')) {
    statusClass += 'status-cancelled';
    icon = '🚫';
    displayText = statusLower.includes('refund') ? 'Refunded' : 'Cancelled';
  }
  // Created/New statuses
  else if (statusLower.includes('created') || 
           statusLower.includes('new') ||
           statusLower.includes('placed')) {
    statusClass += 'status-new';
    icon = '📝';
    displayText = statusLower.includes('created') ? 'Created' : 
                 statusLower.includes('placed') ? 'Placed' : 'New';
  }
  // Under Review/Verification statuses
  else if (statusLower.includes('review') || 
           statusLower.includes('verification') ||
           statusLower.includes('verify')) {
    statusClass += 'status-review';
    icon = '🔍';
    displayText = statusLower.includes('verification') ? 'Verification' : 'Under Review';
  }
  // Shipped/Dispatched statuses
  else if (statusLower.includes('shipped') || 
           statusLower.includes('dispatched') ||
           statusLower.includes('transit')) {
    statusClass += 'status-shipped';
    icon = '🚚';
    displayText = statusLower.includes('dispatched') ? 'Dispatched' : 
                 statusLower.includes('transit') ? 'In Transit' : 'Shipped';
  }
  // Default fallback
  else {
    statusClass += 'status-default';
    icon = '📋';
    displayText = status;
  }
  
  return (
    <span className={statusClass}>
      {icon && <span className="status-icon">{icon}</span>}
      {displayText}
    </span>
  );
}

// Helper for delivery status with more specific styling
function getDeliveryStatusBadge(deliveryStatus, deliveryAttempts = 0) {
  if (!deliveryStatus) return null;
  
  const statusLower = deliveryStatus.toLowerCase();
  let statusClass = 'status-badge ';
  let displayText = deliveryStatus;
  
  if (statusLower === 'delivered') {
    statusClass += 'status-completed';
    displayText = '✓ Delivered';
  } else if (statusLower === 'processing') {
    statusClass += 'status-processing';
    displayText = '⏳ Processing';
  } else if (statusLower === 'pending') {
    statusClass += 'status-pending';
    displayText = '⏳ Pending';
  } else if (statusLower === 'failed') {
    statusClass += 'status-failed';
    displayText = `❌ Failed ${deliveryAttempts > 0 ? `(${deliveryAttempts} attempts)` : ''}`;
  }
  
  return <span className={statusClass}>{displayText}</span>;
}

const PAGE_SIZE = 10;

const OrderHistory = () => {
  const userid = JSON.parse(localStorage.getItem("user"))?.userid;
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/orders/${userid}`);
        if (res.data.status === "success") {
          setOrders(res.data.data || []);
        } else {
          setOrders([]);
        }
      } catch (err) {
        setOrders([]);
      }
      setLoading(false);
    };
    if (userid) fetchOrders();
  }, [userid]);

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(orders.length / PAGE_SIZE));
  const paginatedOrders = orders.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Reset to first page if orders data changes and current page is out of range

  // Always start at page 1 when component mounts
  useEffect(() => {
    setPage(1);
  }, []);

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [orders, totalPages, page]);

  // Handle invoice download
  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await api.download(`/invoices/download/${orderId}`);

      // Extract filename from header
      let filename = `invoice_${orderId}.pdf`;
      const disposition = response.headers["content-disposition"];
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Invoice download error:", err);
      alert("Could not download invoice. " + (err?.message || ""));
    }
  };

  // In-app browser detection helper
  // Device detection helper
  const getMobileOS = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(ua)) return "Android";
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "iOS";
    return "Other";
  };

  // In-app browser detection, but only for non-iPhone devices
  const isInAppBrowser = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const os = getMobileOS();
    // Only return true if not iPhone/iOS
    if (os === "iOS") return false;
    return IN_APP_BROWSERS.test(ua);
  };

  // Handle copy download link for in-app browsers
  const handleCopyDownloadLink = async (order) => {
    try {
      if (!order.remedy_pdf_name && !order.report_filename) {
        alert("PDF not available for this order");
        return;
      }

      // Request a one-time download token
      const { data } = await api.get(API_ENDPOINTS.REMEDY_PDF_TOKEN(order.id));
      
      if (data?.token) {
        // Build frontend download URL
        const frontendBaseUrl = window.location.origin;
        const downloadUrl = `${frontendBaseUrl}/download/${order.id}?token=${data.token}`;
        
        // Copy to clipboard
        await navigator.clipboard.writeText(downloadUrl);
        alert("Download link copied! Open it in your browser to download the PDF.");
        console.log("Download link copied to clipboard:", downloadUrl);
      } else {
        alert("Could not generate download link. Please try again.");
      }
    } catch (err) {
      console.error("Copy link error:", err);
      
      // Fallback for older browsers that don't support clipboard API
      if (err.name === 'NotAllowedError' || !navigator.clipboard) {
        alert("Unable to copy automatically. Please enable clipboard permissions or use a different browser.");
      } else {
        alert("Could not copy download link. " + (err?.message || ""));
      }
    }
  };

  // Handle remedy PDF download
  const handleDownloadRemedyPdf = async (order) => {
    try {
      if (!order.remedy_pdf_name  && !order.report_filename) {
        alert("Remedy PDF not available for this order");
        return;
      }

      // Detect in-app browser
      if (isInAppBrowser()) {
        // For in-app browsers, use token-based download
        try {
          // Step 1: Request a one-time download token
          const { data } = await api.get(API_ENDPOINTS.REMEDY_PDF_TOKEN(order.id));
          
          if (data?.token) {
            // Step 2: Build frontend download URL (keeps user on frontend domain)
            const frontendBaseUrl = window.location.origin;
            const downloadUrl = `${frontendBaseUrl}/download/${order.id}?token=${data.token}`;
            
            // Open in new tab (will prompt user to choose browser in in-app browsers)
            window.open(downloadUrl, "_blank");
            
            console.log("Download link opened in external browser");
          } else {
            alert("Could not generate download link. Please try again.");
          }
        } catch (tokenError) {
          console.error("Token generation error:", tokenError);
          alert("Could not generate download link. " + (tokenError?.message || ""));
        }
        return;
      }

      // For normal browsers, use the existing blob download approach
      const response = await api.download(API_ENDPOINTS.REMEDY_PDF_STREAM(order.id));

      // Extract filename from header or use default
      let filename = order.remedy_pdf_name || `remedy_${order.id}.pdf`;
    
      // Try different header case variations
      const disposition = response.headers["content-disposition"] || 
                         response.headers["Content-Disposition"] ||
                         response.headers.get?.("content-disposition");
   
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Remedy PDF download error:", err);
      alert("Could not download remedy PDF. " + (err?.message || ""));
    }
  };

  return (
    <div className="order-history-page">
      <div className="order-history-container">
        {/* Header Section */}
        <div className="order-history-header">
          <div className="order-history-header-content">
            <h1 className="order-history-title">
              <span className="order-history-icon">📋</span>
              Order History
            </h1>
            <p className="order-history-subtitle">
              Track and manage your astrology service orders
            </p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="order-history-loading">
            <div className="spinner"></div>
            <span>Loading your orders...</span>
          </div>
        ) : orders.length === 0 ? (
          <div className="order-history-empty">
            <div className="empty-icon">📦</div>
            <h3>No orders found</h3>
            <p>You haven't placed any orders yet. Start exploring our services!</p>
          </div>
        ) : isMobile ? (
          // Mobile: Card view
          <div className="order-history-mobile">
            {paginatedOrders.map((order, idx) => (
              <div key={order.id || idx} className="order-card">
                <div className="order-card-header">
                  <div className="order-card-id">{formatOrderId(order.id)}</div>
                  <div className="order-card-amount">₹{order.amount || "-"}</div>
                </div>
                
                <div className="order-card-content">
                  <div className="order-card-row">
                    <span className="order-card-label">
                      <span className="order-card-label-icon">�</span>
                      Date:
                    </span>
                    <span className="order-card-value">{formatDate(order.created_at)}</span>
                  </div>
                  
                  <div className="order-card-row">
                    <span className="order-card-label">
                      <span className="order-card-label-icon">📊</span>
                      Status:
                    </span>
                    <span className="order-card-value">
                      {getStatusBadge(order.status)}
                    </span>
                  </div>
                </div>

                {/* View Details Button */}
                <div className="order-card-footer">
                  <button 
                    className="btn-view-details"
                    onClick={() => navigate(`/consultant/order-details/${order.id}`)}
                  >
                    <ViewIcon size={16} />
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Desktop: Table view
          <div className="order-history-table-wrapper">
            <table className="order-history-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order, idx) => (
                  <tr key={order.id || idx}>
                    <td className="order-id-cell">{formatOrderId(order.id)}</td>
                    <td className="date-cell">{formatDate(order.created_at)}</td>
                    <td className="amount-cell">₹{order.amount || "-"}</td>
                    <td>{getStatusBadge(order.status)}</td>
                    <td className="actions-cell">
                      <button
                        className="btn-view-details-table"
                        onClick={() => navigate(`/consultant/order-details/${order.id}`)}
                        title="View order details"
                      >
                        <ViewIcon size={16} />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {orders.length > 0 && (
          <div className="order-history-pagination">
            <button
              className="pagination-button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              ← Previous
            </button>
            <span className="pagination-info">
              Page {page} of {totalPages}
            </span>
            <button
              className="pagination-button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;